/**
 * Guards single-use partner handover tokens (Amazon SAML, Transcarent OTT)
 * against being exchanged twice.
 *
 * The backend consumes these tokens on first exchange, so a second attempt 401s
 * and shows the member a failure on a handover that already succeeded. A React
 * `useRef` cannot prevent this: the common cause is a Back button or a reload,
 * which mounts a brand new component tree. State therefore has to outlive the
 * page, which is what sessionStorage gives us — scoped to the tab, so opening a
 * fresh partner link in a new tab still works.
 *
 * The key is deliberately outside the `9am-{userId}-` namespace so
 * StorageController.clearUser()/clearAllUsers() does not wipe it — the handover
 * flow calls prepareForExternalTokenLogin(), which clears all users, part-way
 * through the very sequence this guard protects.
 */

const STORAGE_KEY = "auth.usedOneTimeTokens";

const MAX_REMEMBERED_TOKENS = 10;

export type OneTimeTokenFlow = "ott" | "saml";

/**
 * FNV-1a. The tokens are live credentials and sessionStorage is readable by
 * anything running in the tab, so we persist a digest rather than the JWT. Only
 * equality matters here, so a non-crypto hash is enough and stays synchronous.
 */
const digest = (value: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
};

const entryFor = (flow: OneTimeTokenFlow, token: string): string =>
  `${flow}:${digest(token)}`;

const readUsedTokens = (): string[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
};

const writeUsedTokens = (entries: string[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage unavailable — fail open, see claimOneTimeToken.
  }
};

/**
 * Claim a token before exchanging it. Returns false when it has already been
 * claimed in this tab, meaning the caller must NOT send it again.
 *
 * Read and write happen in one call so two callers cannot both see it as
 * unclaimed. Fails open: if storage is unavailable the claim succeeds, because
 * blocking a member's first legitimate handover is far worse than allowing a
 * duplicate exchange.
 */
export const claimOneTimeToken = (
  flow: OneTimeTokenFlow,
  token: string
): boolean => {
  if (!token) {
    return false;
  }

  const entry = entryFor(flow, token);
  const used = readUsedTokens();

  if (used.includes(entry)) {
    return false;
  }

  writeUsedTokens([...used, entry].slice(-MAX_REMEMBERED_TOKENS));
  return true;
};

/**
 * Release a claim so the token can be retried. Called when the exchange fails:
 * a network blip should not permanently burn a token the backend never saw.
 */
export const releaseOneTimeToken = (
  flow: OneTimeTokenFlow,
  token: string
): void => {
  if (!token) {
    return;
  }

  const entry = entryFor(flow, token);
  const used = readUsedTokens();
  const remaining = used.filter((candidate) => candidate !== entry);

  if (remaining.length !== used.length) {
    writeUsedTokens(remaining);
  }
};

export const clearOneTimeTokenGuard = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
};
